/**
 * Utilitaires pour la gestion des données d'XP
 */

/**
 * Récupère le résumé d'XP quotidien pour un utilisateur
 * @param {number} userId - ID de l'utilisateur
 * @returns {Promise<Object>} - Données d'XP ou erreur
 */
export async function fetchDailyXpSummary(userId) {
    try {
        const response = await fetch("/api/xp/daily-summary", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                userId,
            }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return { data: data.summary, error: null };
    } catch (error) {
        console.error("Erreur lors de la récupération des données XP:", error);
        return { data: null, error: error.message };
    }
}

/**
 * Transforme les données d'API en format adapté pour le graphique
 * @param {Array} apiData - Données brutes de l'API
 * @param {Array} dayNames - Noms des jours de la semaine
 * @param {string} todayName - Libellé pour "aujourd'hui"
 * @param {string} yesterdayName - Libellé pour "hier"
 * @param {string} userTimezone - Timezone de l'utilisateur
 * @returns {Array} - Données formatées pour le graphique
 */
export function transformXpDataForChart(
    apiData,
    dayNames = ["D", "L", "M", "M", "J", "V", "S"],
    todayName = "Today",
    yesterdayName = "Yesterday",
    userTimezone = null,
) {
    if (!apiData || !Array.isArray(apiData)) {
        return [];
    }

    // Créer un tableau des 7 derniers jours dans la timezone de l'utilisateur
    const today = userTimezone
        ? new Date(
              new Date().toLocaleString("en-US", { timeZone: userTimezone }),
          )
        : new Date();
    const last7Days = [];

    for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateString = date.toISOString().split("T")[0]; // Format YYYY-MM-DD

        // Trouver les données correspondantes dans l'API
        const dayData = apiData.find((item) => item.day === dateString);

        // Nom du jour - "Today" pour aujourd'hui, "Hier" pour hier, sinon nom du jour
        const isToday = i === 0;
        const isYesterday = i === 1;
        const dayName = isToday
            ? todayName
            : isYesterday
            ? yesterdayName
            : dayNames[date.getDay()];

        last7Days.push({
            day: dayName,
            fullDate: dateString,
            xp: dayData ? parseInt(dayData.total_xp) || 0 : 0,
        });
    }

    return last7Days;
}

/**
 * Récupère le résumé d'XP quotidien pour plusieurs utilisateurs
 * @param {Array} userIds - Tableau d'IDs d'utilisateurs
 * @param {string} userTimezone - Timezone de l'utilisateur
 * @param {boolean} showAllTime - Si true, récupère toutes les données depuis le début
 * @param {string} startDate - Date de début pour filtrer les données (format YYYY-MM-DD)
 * @returns {Promise<Object>} - Données d'XP pour tous les utilisateurs ou erreur
 */
export async function fetchMultipleUserXpSummary(userIds, userTimezone, showAllTime = false, startDate = null) {
    try {
        const response = await fetch("/api/xp/multi-summary", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                userIds,
                userTimezone,
                showAllTime,
                startDate,
            }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return { data: data.users, error: null };
    } catch (error) {
        console.error(
            "Erreur lors de la récupération des données XP multi-utilisateurs:",
            error,
        );
        return { data: null, error: error.message };
    }
}

/**
 * Transforme les données d'API pour afficher toute la progression depuis le début
 * @param {Array} apiData - Données brutes de l'API
 * @param {Array} dayNames - Noms des jours de la semaine
 * @param {string} todayName - Libellé pour "aujourd'hui"
 * @param {string} yesterdayName - Libellé pour "hier"
 * @param {string} userTimezone - Timezone de l'utilisateur
 * @param {string} locale - Locale de l'utilisateur
 * @param {boolean} forceDaily - Si true, force l'affichage jour par jour même si > 30 jours
 * @returns {Array} - Données formatées pour le graphique
 */
export function transformXpDataForAllTime(
    apiData,
    dayNames = ["D", "L", "M", "M", "J", "V", "S"],
    todayName = "Today",
    yesterdayName = "Yesterday",
    userTimezone = null,
    locale = "fr",
    forceDaily = false,
) {
    if (!apiData || !Array.isArray(apiData)) {
        return [];
    }

    // Trier les données par date
    const sortedData = [...apiData].sort((a, b) =>
        new Date(a.day) - new Date(b.day)
    );

    // Si nous avons plus de 30 jours de données ET que forceDaily n'est pas activé, nous allons agréger par semaine
    if (sortedData.length > 30 && !forceDaily) {
        const weeks = {};

        sortedData.forEach(item => {
            const date = new Date(item.day);
            // Obtenir le premier jour de la semaine (lundi)
            const weekStart = new Date(date);
            const day = weekStart.getDay();
            const diff = weekStart.getDate() - day + (day == 0 ? -6 : 1);
            weekStart.setDate(diff);
            weekStart.setHours(0, 0, 0, 0);

            const weekKey = weekStart.toISOString().split("T")[0];

            if (!weeks[weekKey]) {
                weeks[weekKey] = {
                    weekStart: weekKey,
                    totalXp: 0,
                    dayCount: 0,
                };
            }

            weeks[weekKey].totalXp += parseInt(item.total_xp) || 0;
            weeks[weekKey].dayCount++;
        });

        // Formatter les semaines pour l'affichage
        const weekData = Object.values(weeks).map(week => {
            const date = new Date(week.weekStart);
            const options = locale === "fr"
                ? { day: "numeric", month: "short" }
                : { month: "short", day: "numeric" };
            const formattedDate = date.toLocaleDateString(locale, options);

            return {
                day: formattedDate,
                fullDate: week.weekStart,
                xp: week.totalXp,
            };
        });

        return weekData;
    }

    // Si nous avons 30 jours ou moins, afficher tous les jours
    const today = userTimezone
        ? new Date(
              new Date().toLocaleString("en-US", { timeZone: userTimezone }),
          )
        : new Date();
    const todayString = today.toISOString().split("T")[0];
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayString = yesterday.toISOString().split("T")[0];

    return sortedData.map(item => {
        const date = new Date(item.day);
        let dayLabel;

        if (item.day === todayString) {
            dayLabel = todayName;
        } else if (item.day === yesterdayString) {
            dayLabel = yesterdayName;
        } else {
            // Formatter la date selon la locale
            const options = locale === "fr"
                ? { day: "numeric", month: "short" }
                : { month: "short", day: "numeric" };
            dayLabel = date.toLocaleDateString(locale, options);
        }

        return {
            day: dayLabel,
            fullDate: item.day,
            xp: parseInt(item.total_xp) || 0,
        };
    });
}

/**
 * Calcule le total d'XP pour une période donnée
 * @param {Array} chartData - Données formatées du graphique
 * @returns {number} - Total d'XP
 */
export function calculateTotalXp(chartData) {
    if (!chartData || !Array.isArray(chartData)) {
        return 0;
    }

    return chartData.reduce((total, day) => total + (day.xp || 0), 0);
}
